import type { RunRowLike } from "@/types/RunRow"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import type { CompareByDimension } from "../types"

export type { CompareByDimension } from "../types"

/**
 * Multi-run compare configuration shared between table and consumers.
 *
 * - dimension: agent | mutation | persona | test_case | metadata
 * - metadataKey: when dimension === "metadata"
 * - colors: value key (agentId / personaId / test case label / metadata value string) -> hex; missing = default text color
 */

export interface CompareBy {
  dimension: CompareByDimension
  metadataKey?: string
  colors: Record<string, string>
  /** Optional map from group key (e.g. agentId) to display name for legends/labels. */
  displayLabels?: Record<string, string>
  /** Map from simulation_output_id → custom group id. Required when dimension === "custom". */
  customGroupBySimId?: Record<string, string>
  /** Map from run_id → the run's tags (from evals_run.tags). Populated by
   *  the caller because rows themselves don't carry run-level tags. Only
   *  read when dimension === "tag". */
  tagsByRunId?: Record<string, string[]>
}

/** Sentinel group key used for simulations not belonging to any group in the active custom dimension. Renders as a trailing "Unassigned" bucket. */
export const CUSTOM_UNASSIGNED_KEY = "__custom_unassigned__"

/** Sentinel group key used for simulations whose run has no tags. Renders as
 *  a trailing "Untagged" bucket for dimension === "tag". Uppercased so it
 *  cannot collide with any user-authored tag: the tag normalization
 *  contract (see @/tags normalizeTag) lowercases every stored tag, so no
 *  real tag can equal this literal. */
export const TAG_UNTAGGED_KEY = "__TAG_UNTAGGED__"

/** Get the compare-by group key for a run row (e.g. agentId, personaId). Used to group metric outputs for comparison charts.
 *
 * For dimension === "tag", returns the row's first tag (or the "untagged"
 * sentinel). Multi-tag callers should use getGroupKeysForRow instead — a
 * row with two tags belongs in both groups, and this single-key entry
 * point can only return one.
 */
export function getGroupKeyForRow(
  row: RunRowLike,
  compareBy: CompareBy,
): string | null {
  if (compareBy.dimension === "agent") {
    return row.agent_id ? row.agent_id : null
  }
  if (compareBy.dimension === "persona") {
    return row.persona_id ? row.persona_id : null
  }
  if (compareBy.dimension === "test_case") {
    return row.test_set_row_id ?? null
  }
  if (compareBy.dimension === "mutation") {
    return row.mutation_id ?? null
  }
  if (compareBy.dimension === "run") {
    return row.run_id ?? null
  }
  if (compareBy.dimension === "metadata" && compareBy.metadataKey) {
    const raw = row.customer_metadata?.[compareBy.metadataKey]
    return raw !== undefined && raw !== null ? stringifyUnknownValue(raw) : null
  }
  if (compareBy.dimension === "custom") {
    const simId = row.simulation_output_id
    if (!simId) {
      return null
    }
    return compareBy.customGroupBySimId?.[simId] ?? CUSTOM_UNASSIGNED_KEY
  }
  if (compareBy.dimension === "tag") {
    const keys = getGroupKeysForRow(row, compareBy)
    return keys[0] ?? null
  }
  return null
}

/** Multi-key variant. Same as getGroupKeyForRow for single-value dimensions,
 *  but for dimension === "tag" a row with N run-level tags returns N keys
 *  so it appears under each of its tag groups. Untagged rows return a
 *  one-element array with the sentinel. */
export function getGroupKeysForRow(
  row: RunRowLike,
  compareBy: CompareBy,
): string[] {
  if (compareBy.dimension === "tag") {
    const tags = row.run_id ? (compareBy.tagsByRunId?.[row.run_id] ?? []) : []
    if (tags.length === 0) {
      return [TAG_UNTAGGED_KEY]
    }
    return tags
  }
  const key = getGroupKeyForRow(row, compareBy)
  return key == null ? [] : [key]
}

/** Numeric-aware, case-insensitive comparator shared by group-key, categorical-axis, and legend ordering. */
export function compareGroupLabels(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
}

/** All unique compare-by group keys present in run rows (e.g. all agent IDs). Use so charts include every dimension even when a group has no metric outputs.
 *
 * For dimension === "tag" a single row contributes every tag it carries,
 * so this is a flatMap-with-dedup rather than a straight iteration.
 */
export function getCompareByGroupKeys(
  runRows: RunRowLike[],
  compareBy: CompareBy,
): string[] {
  const seen = new Set<string>()
  const keys: string[] = []
  for (const row of runRows) {
    for (const key of getGroupKeysForRow(row, compareBy)) {
      if (!seen.has(key)) {
        seen.add(key)
        keys.push(key)
      }
    }
  }
  return keys.sort(compareGroupLabels)
}

/** Group metric outputs by compare-by key (e.g. by agentId). Outputs without a matching run row or group key are skipped.
 *
 * For dimension === "tag" a row's metric output appears in every one of
 * the row's tag groups (a two-tag row contributes to both buckets).
 */
export function groupMetricOutputsByCompareBy<
  T extends { simulation_output_id: string },
>(
  metricOutputs: T[],
  runRows: RunRowLike[],
  compareBy: CompareBy,
): Map<string, T[]> {
  const rowBySimId = new Map(runRows.map((r) => [r.simulation_output_id, r]))
  const groups = new Map<string, T[]>()
  for (const mo of metricOutputs) {
    const row = rowBySimId.get(mo.simulation_output_id)
    if (!row) {
      continue
    }
    for (const key of getGroupKeysForRow(row, compareBy)) {
      const existing = groups.get(key)
      if (existing) {
        existing.push(mo)
      } else {
        groups.set(key, [mo])
      }
    }
  }
  return groups
}
